import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, Sparkles, BookOpen, Hammer, CheckCircle2, Mic, ClipboardCheck,
  Trophy, MessageCircle, Printer, Compass, ArrowRight, Loader2,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1585432959445-662c9bbcd91d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const COZY = "https://images.unsplash.com/photo-1579891877859-9545565a353f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const GROUP_META = {
  core: { label: "Core Subjects", blurb: "The academic backbone — the subjects every student masters from Kindergarten through 12th grade.", color: "#3B82F6", icon: BookOpen },
  life_skills: { label: "Real-World Life Skills", blurb: "Everything they actually need after graduation — money, work, home, health, and independence. Grades 9–12.", color: "#CA8A04", icon: Hammer },
  college_prep: { label: "College Prep & Career Technical", blurb: "A head start on college-level learning and hands-on career training. Optional premium electives.", color: "#7C3AED", icon: GraduationCap },
};

const FEATURES = [
  { icon: MessageCircle, title: "AI Tutor", text: "A patient, lesson-aware tutor your student can ask anything, any time — it knows exactly what they're studying." },
  { icon: Mic, title: "Voice Narration", text: "Lessons and practice can be read aloud, so early and struggling readers never get left behind." },
  { icon: ClipboardCheck, title: "Auto-Graded Work", text: "Quizzes and assignments grade themselves instantly, with friendly feedback and forgiving short-answer checking." },
  { icon: Trophy, title: "Progress Tracking", text: "A parent gradebook tracks every quiz and assignment — scores, dates, and subjects, all in one place." },
  { icon: Sparkles, title: "Interactive Lessons", text: "Play-and-learn activities make each lesson hands-on and genuinely fun, not just pages to read." },
  { icon: Printer, title: "Printable Workbooks", text: "Prefer paper? Print clean worksheets and answer keys for off-screen days." },
];

export default function CurriculumSales() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/curriculum/overview").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const totals = data?.totals;
  const groups = data?.groups;

  return (
    <div className="kid-theme min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden border-b-2 border-border bg-accent">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-6xl px-5 py-20 lg:px-8 lg:py-28">
          <span className="label-caps text-secondary" data-testid="sales-eyebrow">Academically Strong · Prepared for Life</span>
          <h1 className="mt-3 max-w-3xl font-head text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
            A Complete K–12 Homeschool Curriculum Built for Real Families
          </h1>
          <p className="mt-5 max-w-2xl text-base font-bold text-foreground/70 sm:text-lg">
            Faith-rooted academics, real-world life skills, and career-ready electives — all in one place, with an AI tutor in every lesson. Rugged, grounded, and ready the day you enroll.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/pricing" data-testid="sales-enroll-btn">
              <Button className="rounded-full bg-primary px-8 py-7 text-base font-extrabold text-white tactile hover:bg-primary">
                Enroll Now <ArrowRight className="ml-2 h-5 w-5" strokeWidth={2.5} />
              </Button>
            </Link>
            <Link to="/curriculum/browse" data-testid="sales-browse-btn">
              <Button variant="outline" className="rounded-full border-2 px-8 py-7 text-base font-extrabold">
                <Compass className="mr-2 h-5 w-5" strokeWidth={2.5} /> Browse the Full Catalog
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-b-2 border-border bg-card">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-5 py-10 lg:grid-cols-4 lg:px-8">
          {[
            { n: totals ? totals.grades : "13", l: "Grades (K–12)" },
            { n: totals ? totals.subjects : "—", l: "Subjects" },
            { n: totals ? totals.courses : "—", l: "Courses" },
            { n: totals ? `${totals.lessons}+` : "—", l: "Lessons" },
          ].map((s, i) => (
            <div key={i} className="text-center" data-testid={`sales-stat-${i}`}>
              <p className="font-head text-4xl font-black text-primary sm:text-5xl">{s.n}</p>
              <p className="mt-1 label-caps text-muted-foreground">{s.l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="max-w-2xl">
          <span className="label-caps text-secondary">What's Inside</span>
          <h2 className="mt-2 font-head text-3xl font-black sm:text-4xl">Everything your family needs to teach with confidence</h2>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <div key={i} className="rounded-3xl border-2 border-border bg-card p-6 transition-transform hover:-translate-y-1" data-testid={`sales-feature-${i}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary"><f.icon className="h-6 w-6" strokeWidth={2.5} /></span>
              <h3 className="mt-4 font-head text-xl font-black">{f.title}</h3>
              <p className="mt-2 text-sm font-semibold text-muted-foreground">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* AI Tutor highlight */}
      <section className="border-y-2 border-border bg-accent/40">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:px-8">
          <div>
            <span className="label-caps text-secondary">Meet Your AI Tutor</span>
            <h2 className="mt-2 font-head text-3xl font-black sm:text-4xl">A teacher's aide that never clocks out</h2>
            <p className="mt-4 text-base font-bold text-foreground/70">
              Stuck on a math problem at 9pm? The AI Tutor already knows which lesson your student is in and explains it in plain language — step by step, as many times as it takes. It's like having a tutor on call for every subject, without the hourly rate.
            </p>
            <ul className="mt-6 space-y-3">
              {["Knows the exact lesson being studied", "Explains, quizzes, and encourages", "Available for every grade and subject"].map((t, i) => (
                <li key={i} className="flex items-center gap-3 font-bold"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" strokeWidth={2.5} /> {t}</li>
              ))}
            </ul>
            <Link to="/pricing"><Button className="mt-8 rounded-full bg-primary px-7 py-6 font-extrabold text-white tactile hover:bg-primary">Start Learning</Button></Link>
          </div>
          <div className="overflow-hidden rounded-3xl border-2 border-border shadow-sm">
            <img src={COZY} alt="Student learning at home" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Subject groups */}
      <section className="mx-auto max-w-6xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="max-w-2xl">
          <span className="label-caps text-secondary">The Full Scope</span>
          <h2 className="mt-2 font-head text-3xl font-black sm:text-4xl">Every subject, from reading and math to a real-world career</h2>
          <p className="mt-3 text-base font-bold text-foreground/70">New courses are added all the time — subjects marked <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-black uppercase text-amber-700">Coming Soon</span> are on the way.</p>
        </div>

        {!data ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <div className="mt-10 space-y-8">
            {["core", "life_skills", "college_prep"].map((g) => {
              const meta = GROUP_META[g];
              const items = groups?.[g] || [];
              if (!items.length) return null;
              const Icon = meta.icon;
              return (
                <div key={g} className="rounded-3xl border-2 border-border bg-card p-6 sm:p-8" data-testid={`sales-group-${g}`}>
                  <div className="flex items-start gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}><Icon className="h-6 w-6" strokeWidth={2.5} /></span>
                    <div>
                      <h3 className="font-head text-2xl font-black" style={{ color: meta.color }}>{meta.label}</h3>
                      <p className="mt-1 max-w-2xl text-sm font-semibold text-muted-foreground">{meta.blurb}</p>
                    </div>
                  </div>
                  <div className="mt-6 flex flex-wrap gap-2.5">
                    {items.map((s) => {
                      const soon = !s.course_count;
                      return (
                        <span
                          key={s.subject_id}
                          data-testid={`sales-subject-${s.subject_id}`}
                          className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-extrabold ${soon ? "border-dashed border-border bg-muted/40 text-muted-foreground" : "border-border bg-background text-foreground"}`}
                        >
                          {s.name}
                          {soon && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[0.6rem] font-black uppercase tracking-wide text-amber-700">Soon</span>}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="border-t-2 border-border bg-primary">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center lg:px-8">
          <h2 className="font-head text-3xl font-black text-white sm:text-4xl">Ready to give your kids a strong, grounded education?</h2>
          <p className="mt-3 text-base font-bold text-white/80">$349/year per grade, or go month-to-month. One login opens the whole library, the AI tutor, and progress tracking.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/pricing" data-testid="sales-cta-pricing"><Button className="rounded-full bg-white px-8 py-7 text-base font-extrabold text-primary hover:bg-white/90">See Pricing &amp; Enroll</Button></Link>
            <Link to="/curriculum/browse"><Button variant="outline" className="rounded-full border-2 border-white/60 bg-transparent px-8 py-7 text-base font-extrabold text-white hover:bg-white/10">Browse the Catalog</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
